import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Archive } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/Components/ui/Button';
import { Card } from '@/Components/ui/Card';
import { Checkbox } from '@/Components/ui/Checkbox';
import { Input } from '@/Components/ui/Input';
import { Select } from '@/Components/ui/Select';
import { PageHeader } from '@/Components/data/PageHeader';
import DecimalInput from '@/Components/forms/DecimalInput';
import { Field } from '@/Components/forms/Field';
import { FormStickyFooter } from '@/Components/forms/FormStickyFooter';
import produtoService, { type ProdutoInput } from '@/services/produto';
import tipoProdutoService, { getTipoProdutoUuid, type TipoProduto } from '@/services/tipoProduto';
import unidadeMedidaService, { type UnidadeMedida } from '@/services/unidadeMedida';
import validadeHistoricoService, { type ValidadeHistoricoItem } from '@/services/validadeHistorico';

const FORM_VAZIO: ProdutoInput = {
  codigo: '',
  descricao: '',
  tipo_produto_id: null,
  unidade: '',
  controla_lote: true,
  dias_validade: null,
  ncm: '',
  custo_padrao: 0,
  estoque_minimo: 0,
  ativo: true,
  codigo_barras: '',
  codigo_erp: '',
};

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="nx-sngpc-form-section">
      <h2>{title}</h2>
      <div className="nx-form-grid nx-sngpc-page-form-grid">{children}</div>
    </section>
  );
}

export default function ProdutoForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const editando = Boolean(id);
  const [form, setForm] = useState<ProdutoInput>(FORM_VAZIO);
  const [tiposProduto, setTiposProduto] = useState<TipoProduto[]>([]);
  const [unidades, setUnidades] = useState<UnidadeMedida[]>([]);
  const [historico, setHistorico] = useState<ValidadeHistoricoItem[]>([]);
  const [loading, setLoading] = useState(editando);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    tipoProdutoService.listar({ industrializacao: true }).then(setTiposProduto).catch(console.error);
    unidadeMedidaService.listar().then(setUnidades).catch(console.error);
  }, []);

  const tiposProdutoValidos = tiposProduto.filter((item) => getTipoProdutoUuid(item));
  const tipoProdutoSelecionadoValido = !form.tipo_produto_id || tiposProdutoValidos.some((item) => getTipoProdutoUuid(item) === form.tipo_produto_id);

  useEffect(() => {
    if (!id) return;

    setLoading(true);
    produtoService.obter(id)
      .then((item) => {
        setForm({
          codigo: item.codigo,
          descricao: item.descricao,
          tipo_produto_id: item.tipo_produto_id ?? item.tipo_produto ?? null,
          unidade: item.unidade,
          controla_lote: item.controla_lote,
          dias_validade: item.dias_validade ?? null,
          ncm: item.ncm ?? '',
          custo_padrao: item.custo_padrao ?? 0,
          estoque_minimo: item.estoque_minimo ?? 0,
          ativo: item.ativo,
          codigo_barras: item.codigo_barras ?? '',
          codigo_erp: item.codigo_erp ?? '',
        });
      })
      .catch((requestError) => {
        console.error('Erro ao carregar produto', requestError);
        setError('Não foi possível carregar o produto.');
      })
      .finally(() => setLoading(false));

    validadeHistoricoService.porProduto(id).then(setHistorico).catch(console.error);
  }, [id]);

  const set = <TKey extends keyof ProdutoInput>(key: TKey, value: ProdutoInput[TKey]) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form.descricao || !form.unidade) return;
    if (!editando && !tipoProdutoSelecionadoValido) {
      setError('Tipo de produto inválido. Atualize a lista de tipos de produto antes de salvar.');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      if (id) {
        const { tipo_produto_id, ...payload } = form;
        await produtoService.atualizar(id, payload);
      } else {
        await produtoService.criar(form);
      }
      navigate('/producao/produtos');
    } catch (requestError) {
      console.error('Erro ao salvar produto', requestError);
      setError('Não foi possível salvar o produto.');
    } finally {
      setSaving(false);
    }
  }

  async function inativar() {
    if (!id) return;
    setSaving(true);
    setError(null);
    try {
      await produtoService.atualizar(id, { ativo: false });
      navigate('/producao/produtos');
    } catch (requestError) {
      console.error('Erro ao inativar produto', requestError);
      setError('Não foi possível inativar o produto.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="nx-page nx-sngpc-form-page" onSubmit={submit}>
      <PageHeader
        icon={<Archive size={21} strokeWidth={2} />}
        title={editando ? 'Editar Produto' : 'Novo Produto'}
        description="Dados cadastrais, controle de estoque e classificação do item industrializado."
      />

      {error ? <div className="nx-sngpc-error" role="alert">{error}</div> : null}

      <Card className="nx-page-card nx-sngpc-form-card">
        <Section title="Identificação">
          <Field label="Código" htmlFor="produto-codigo">
            <Input id="produto-codigo" value={editando ? form.codigo || '' : 'Gerado ao salvar'} readOnly />
          </Field>
          <Field label="Código ERP" htmlFor="produto-cod-erp">
            <Input id="produto-cod-erp" value={form.codigo_erp || ''} onChange={(event) => set('codigo_erp', event.target.value)} />
          </Field>
          <Field label="Código de barras" htmlFor="produto-ean">
            <Input id="produto-ean" value={form.codigo_barras || ''} onChange={(event) => set('codigo_barras', event.target.value)} />
          </Field>
          <Field label="Descrição" htmlFor="produto-descricao" className="nx-sngpc-field--wide">
            <Input
              id="produto-descricao"
              value={form.descricao}
              onChange={(event) => set('descricao', event.target.value)}
              required
            />
          </Field>
        </Section>

        <Section title="Classificação e controle">
          <Field label="Tipo" htmlFor="produto-tipo">
            <Select
              id="produto-tipo"
              value={form.tipo_produto_id || ''}
              onChange={(event) => set('tipo_produto_id', event.target.value || null)}
              disabled={editando}
              required
            >
              <option value="">Selecione</option>
              {tiposProdutoValidos.map((item) => (
                <option key={item.id} value={getTipoProdutoUuid(item) || ''}>{item.descricao}</option>
              ))}
            </Select>
            {tiposProduto.length > 0 && tiposProdutoValidos.length === 0 ? (
              <span className="nx-form-hint">Nenhum tipo de produto com UUID válido foi retornado pela API.</span>
            ) : null}
          </Field>
          <Field label="Unidade de medida" htmlFor="produto-unidade">
            <Select
              id="produto-unidade"
              value={form.unidade}
              onChange={(event) => set('unidade', event.target.value)}
              required
            >
              <option value="">Selecione</option>
              {unidades.map((item) => (
                <option key={item.id} value={item.codigo}>{item.codigo} - {item.descricao}</option>
              ))}
            </Select>
          </Field>
          <Field label="NCM/Fiscal" htmlFor="produto-ncm">
            <Input id="produto-ncm" value={form.ncm || ''} onChange={(event) => set('ncm', event.target.value)} />
          </Field>
          <Field label="Dias validade" htmlFor="produto-validade">
            <Input
              id="produto-validade"
              type="number"
              value={form.dias_validade ?? ''}
              onChange={(event) => set('dias_validade', event.target.value === '' ? null : Number(event.target.value))}
            />
          </Field>
          <Field label="Estoque mínimo" htmlFor="produto-estoque-min">
            <DecimalInput
              id="produto-estoque-min"
              value={form.estoque_minimo ?? ''}
              onValueChange={(value) => set('estoque_minimo', value)}
            />
          </Field>
          <Field label="Custo padrão" htmlFor="produto-custo">
            <DecimalInput
              id="produto-custo"
              value={form.custo_padrao ?? ''}
              onValueChange={(value) => set('custo_padrao', value)}
            />
          </Field>
          <div className="nx-sngpc-checks nx-sngpc-field--wide">
            <label>
              <Checkbox checked={form.controla_lote ?? true} onChange={(event) => set('controla_lote', event.target.checked)} />
              Controla lote
            </label>
            <label>
              <Checkbox checked={form.ativo ?? true} onChange={(event) => set('ativo', event.target.checked)} />
              Cadastro ativo
            </label>
          </div>
        </Section>

        {editando ? (
          <Section title="Histórico de validade">
            <div className="nx-table-wrap nx-sngpc-field--full">
              <table className="nx-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Validade Anterior</th>
                    <th>Validade Nova</th>
                    <th>Justificativa</th>
                    <th>Usuário</th>
                  </tr>
                </thead>
                <tbody>
                  {historico.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="nx-table-loading">Nenhum histórico</td>
                    </tr>
                  ) : (
                    historico.map((item) => (
                      <tr key={item.id}>
                        <td>{new Date(item.data_alteracao).toLocaleString('pt-BR')}</td>
                        <td>{item.dias_validade_anterior ?? '-'}</td>
                        <td>{item.dias_validade_novo ?? '-'}</td>
                        <td>{item.justificativa || '-'}</td>
                        <td>{item.responsavel_nome || '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Section>
        ) : null}
      </Card>

      <FormStickyFooter
        loading={saving || loading}
        saveLabel="Salvar"
        backLabel="Cancelar"
        onBack={() => navigate('/producao/produtos')}
        actions={
          editando ? (
            <Button
              type="button"
              variant="danger"
              disabled={saving || loading || form.ativo === false}
              onClick={inativar}
            >
              Inativar
            </Button>
          ) : null
        }
      />
    </form>
  );
}
